import { useState, useEffect } from 'react';
import { Server, Key, Copy, Check, AlertTriangle } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import type { DeviceStatus } from '../../types';
import systemService from '../../services/system.service';
import configTemplateService, { type ConfigTemplate } from '../../services/configTemplate.service';

interface Edge {
  id: string;
  name: string;
  type?: string;
  location: string;
  model: string;
  serialNumber: string;
  ipAddress: string;
  version: string;
  uptime: string;
  cpu: string;
  memory: string;
  lastSeen: string;
  status: DeviceStatus;
}

interface EdgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (edge: Edge, tenantId?: string) => void | Promise<void>;
  edge?: Edge;
  mode: 'add' | 'edit';
  /** Pre-selected tenant ID (for TenantAdmin — hides tenant dropdown) */
  fixedTenantId?: string;
}

const EdgeModal = ({ isOpen, onClose, onSave, edge, mode, fixedTenantId }: EdgeModalProps) => {
  const toast = useToast();
  const [formData, setFormData] = useState<Edge>(
    edge || {
      id: '',
      name: '',
      location: '',
      model: 'VSR1000',
      serialNumber: '',
      ipAddress: '',
      version: '2.4.5',
      uptime: '',
      cpu: '',
      memory: '',
      lastSeen: '',
      status: 'Offline',
    }
  );
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [templates, setTemplates] = useState<ConfigTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [deploymentToken, setDeploymentToken] = useState<string>('');
  const [tokenExpiry, setTokenExpiry] = useState<string>('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [vppMode, setVppMode] = useState<string>('tap');

  // Sync edge prop into formData when it changes (component stays mounted)
  useEffect(() => {
    if (edge) {
      setFormData(edge);
    }
  }, [edge]);

  // Load tenants and templates on open; clear stale state on close
  useEffect(() => {
    if (isOpen && mode === 'add') {
      if (fixedTenantId) {
        // Tenant is fixed (TenantAdmin OR SuperAdmin with a tenant selected in topbar).
        setSelectedTenant(fixedTenantId);
        loadTemplates(fixedTenantId);
      } else {
        // SuperAdmin with no tenant selected (All Tenants view) — don't load templates.
        setTemplates([]);
        setSelectedTemplate('');
      }
    }
    if (!isOpen) {
      setDeploymentToken('');
      setTokenExpiry('');
      setTokenCopied(false);
      setVppMode('tap');
    }
  }, [isOpen, mode, fixedTenantId]);

  const loadTemplates = async (tenantId: string) => {
    try {
      setLoadingTemplates(true);
      const data = await configTemplateService.list({ tenantId });
      setTemplates(data);
      // Select default template if available
      const defaultTemplate = data.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
      } else if (data.length > 0) {
        setSelectedTemplate(data[0].id);
      } else {
        setSelectedTemplate('');
      }
    } catch (error) {
      console.error('Failed to load configuration templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleGenerateToken = async () => {
    if (!selectedTenant) {
      toast.error('Validation Error', 'Please select a tenant first');
      return;
    }

    if (!selectedTemplate) {
      toast.error('Validation Error', 'Please select a configuration template first');
      return;
    }

    try {
      setIsGeneratingToken(true);
      const result = await systemService.generateDeploymentToken(selectedTenant, selectedTemplate, vppMode);
      setDeploymentToken(result.token);
      setTokenExpiry(result.expiresIn);
      setTokenCopied(false);
    } catch (error) {
      console.error('Failed to generate token:', error);
      toast.error('Token Generation Failed', 'Failed to generate deployment token. Please try again.');
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // Check if we can generate tokens
  const canGenerateToken = selectedTenant && selectedTemplate && templates.length > 0;
  const noTemplatesAvailable = !loadingTemplates && templates.length === 0;

  const handleCopyToken = () => {
    if (deploymentToken) {
      navigator.clipboard.writeText(deploymentToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  const handleChange = (field: keyof Edge, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSave(formData, selectedTenant);
    onClose();
  };

  // For "Add" mode, show deployment token workflow
  // For "Edit" mode, show traditional edit form
  const content = mode === 'add' ? (
    <div className="space-y-6">
      {/* No Templates Warning */}
      {noTemplatesAvailable && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-amber-900">No Configuration Templates Available</h4>
              <p className="text-sm text-amber-700 mt-1">
                You must create at least one configuration template before you can deploy edge devices.
                Templates define the initial network configuration that will be applied to new edges.
              </p>
              <a 
                href="/admin/config-templates" 
                className="text-sm font-medium text-amber-800 hover:text-amber-900 underline mt-2 inline-block"
              >
                Go to Configuration Templates
              </a>
            </div>
          </div>
        </div>
      )}

      {/* No-tenant-selected warning — SuperAdmin with "All Tenants" in topbar */}
      {!fixedTenantId && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Select a tenant</h3>
              <p className="mt-1 text-sm text-amber-700">
                Choose a specific tenant in the topbar (not "All Tenants") before adding an edge. The edge and its config template are scoped to that tenant.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Configuration Template <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedTemplate}
          onChange={(e) => setSelectedTemplate(e.target.value)}
          className="input-base w-full"
          disabled={loadingTemplates || noTemplatesAvailable}
        >
          {loadingTemplates ? (
            <option>Loading templates...</option>
          ) : noTemplatesAvailable ? (
            <option>No templates available - please create one first</option>
          ) : (
            templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.edge_model}){template.is_default ? ' - Default' : ''}
              </option>
            ))
          )}
        </select>
        <p className="mt-1 text-sm text-gray-500">
          Select the configuration template to apply when this edge connects
        </p>
      </div>

      {/* VPP Dataplane Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dataplane Mode
        </label>
        <select
          value={vppMode}
          onChange={(e) => setVppMode(e.target.value)}
          className="input-base w-full"
        >
          <option value="tap">TAP (Cloud VMs, virtio NICs)</option>
          <option value="afpacket">AF_PACKET (Shared NIC — kernel + VPP)</option>
          <option value="dpdk">DPDK (Dedicated hardware — VPP owns NICs via PCI passthrough)</option>
        </select>
        <p className="mt-1 text-sm text-gray-500">
          {vppMode === 'tap' && 'Recommended for cloud VMs. VPP connects via TAP interface, Linux retains NIC ownership.'}
          {vppMode === 'afpacket' && 'VPP shares the NIC with Linux via AF_PACKET. Suitable for bare-metal with shared management.'}
          {vppMode === 'dpdk' && 'VPP takes full ownership of NICs via VFIO/PCI passthrough. Highest performance, requires dedicated management NIC.'}
        </p>
      </div>

      {/* Deployment Token Generation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Deployment Token
        </label>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGenerateToken}
            disabled={isGeneratingToken || !canGenerateToken}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Key className="w-4 h-4" />
            <span>{isGeneratingToken ? 'Generating...' : 'Get Token'}</span>
          </button>
          {deploymentToken && (
            <button
              onClick={handleCopyToken}
              className="btn-secondary inline-flex items-center space-x-2"
            >
              {tokenCopied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
        </div>
        {noTemplatesAvailable ? (
          <p className="mt-1 text-sm text-amber-600">
            Token generation disabled - create a configuration template first
          </p>
        ) : (
          <p className="mt-1 text-sm text-gray-500">
            Generate a deployment token to register a new edge device
          </p>
        )}
      </div>

      {/* Display Token */}
      {deploymentToken && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Key className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Deployment Token Generated
              </h4>
              <div className="bg-white border border-blue-300 rounded px-3 py-2 mb-2">
                <code className="text-sm font-mono text-gray-900 break-all">
                  {deploymentToken}
                </code>
              </div>
              <p className="text-sm text-blue-700">
                Token expires in: <span className="font-medium">{tokenExpiry}</span>
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Use this token to register your edge device. The device will use this token to authenticate with the Global VSA during initial deployment.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Deployment Instructions
        </h4>
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
          <li>Select the tenant for the edge device</li>
          <li>Click "Get Token" to generate a deployment token</li>
          <li>Copy the token and provide it to the edge device during setup</li>
          <li>The edge device will use this token to register with the Global VSA</li>
          <li>Once registered, the device will appear in the pending edges list</li>
        </ol>
      </div>
    </div>
  ) : (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Edge Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Edge-NYC-01"
            className="input-base w-full"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type
          </label>
          <select
            value={formData.type || 'Edge'}
            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
            className="input-base w-full"
          >
            <option value="Edge">Edge</option>
            <option value="Cloud Edge">Cloud Edge</option>
            <option value="NaaS Edge">NaaS Edge</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model
          </label>
          <select
            value={formData.model || 'VSR1000'}
            onChange={(e) => handleChange('model', e.target.value)}
            className="input-base w-full"
          >
            <option value="VSR1000">VSR1000</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="e.g., New York, NY"
            className="input-base w-full"
          />
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        {mode === 'add' ? 'Close' : 'Cancel'}
      </button>
      {mode === 'edit' && (
        <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
          <Server className="w-4 h-4" />
          <span>Save Changes</span>
        </button>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add New Edge - Generate Deployment Token' : `Edit Edge: ${edge?.name}`}
      size="2xl"
      footer={footer}
    >
      {content}
    </Modal>
  );
};

export default EdgeModal;

